import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Activity,
  AlertCircle,
  Camera,
  Check,
  ChevronRight,
  CircleStop,
  CloudUpload,
  Loader2,
  Play,
  RefreshCw,
  RotateCcw,
  Send,
  Video,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";
import BrandLogo from "./BrandLogo";
import Dashboard from "./Dashboard";
import PoseOverlay from "./PoseOverlay";
import { useSwingCamera } from "../hooks/useSwingCamera";
import { DETECTOR_VERSION } from "../lib/swingDetector";
import {
  AUTO_SUBMIT_DELAY_SECONDS,
  applyCaptureUploadFailurePolicy,
  beginAutoSubmissionCountdown,
} from "../lib/cameraSubmission";
import {
  deletePendingCapture,
  deletePendingCapturesForSession,
  listPendingCaptures,
  pruneExpiredCaptures,
  savePendingCapture,
} from "../lib/cameraStorage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 2000;
const SESSION_STORAGE_KEY = "rezzil-active-camera-session";
const LOCAL_CAPTURE_TTL_MS = 24 * 60 * 60 * 1000;

const phaseLabels = {
  positioning: "Position golfer",
  address_stable: "Address stable",
  recording: "Camera armed · recording pre-swing",
  swing_active: "Swing detected",
  follow_through: "Follow through",
  validating: "Finalizing clip",
  queued: "Clip queued",
  cooldown: "Ready for next shot",
};

const shotLabels = {
  awaiting_upload: "Uploading",
  queued: "Queued",
  retrying: "Retrying",
  probing: "Checking video",
  analyzing: "Analyzing",
  trimming: "Preparing replay",
  storing: "Saving result",
  complete: "Complete",
  failed: "Needs attention",
};

const errorMessage = (error, fallback) => (
  error?.response?.data?.detail || error?.message || fallback
);

const loadStoredSession = () => {
  try {
    return JSON.parse(window.localStorage.getItem(SESSION_STORAGE_KEY) || "null");
  } catch {
    return null;
  }
};

const CameraSetup = ({ capabilities, onStart, starting, error }) => {
  const [cameraView, setCameraView] = useState("down_the_line");
  return (
    <div className="min-h-screen bg-[#0A0A0A] px-6 py-10 md:px-12">
      <header className="mx-auto flex max-w-6xl items-center justify-between border-b border-[#262626] pb-6">
        <div className="flex items-center gap-3">
          <BrandLogo compact />
          <span className="font-heading text-xl uppercase tracking-wider text-[#A3A3A3]">Live Cam</span>
        </div>
        <a href="/" className="text-sm font-bold uppercase tracking-wider text-[#A3A3A3] hover:text-white">
          Manual upload
        </a>
      </header>

      <main className="mx-auto grid max-w-6xl gap-8 py-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
        <section>
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.24em] text-[#CCFF00]">Automatic capture</p>
          <h1 className="font-heading text-4xl font-black uppercase tracking-tight text-white md:text-6xl">
            {capabilities.target_shots} swings.<br /><span className="text-[#CCFF00]">No upload button.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-[#A3A3A3]">
            The camera detects each swing locally and sends only the short swing clip for analysis. The continuous camera feed is never uploaded.
          </p>
          <div className="mt-8 grid gap-3 sm:grid-cols-3">
            {["Local detection", `${capabilities.target_shots}-shot session`, "24-hour retention"].map((item, index) => (
              <div key={item} className="border border-[#262626] bg-[#121212] p-4">
                <span className="font-mono text-xs text-[#666]">0{index + 1}</span>
                <p className="mt-2 text-sm font-bold uppercase tracking-wide text-white">{item}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="border border-[#262626] bg-[#121212] p-6 md:p-8">
          <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-[#CCFF00]/10">
            <Camera className="h-6 w-6 text-[#CCFF00]" />
          </div>
          <h2 className="font-heading text-2xl font-bold uppercase text-white">Configure camera angle</h2>
          <p className="mt-2 text-sm text-[#A3A3A3]">Mount one fixed camera and keep this angle for the full session.</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {[
              ["down_the_line", "Down the line", "Camera behind the golfer, aligned toward the target."],
              ["face_on", "Face on", "Camera opposite the golfer, perpendicular to the target."],
            ].map(([value, title, description]) => (
              <button
                key={value}
                type="button"
                onClick={() => setCameraView(value)}
                className={`p-4 text-left transition-colors ${cameraView === value ? "border border-[#CCFF00] bg-[#CCFF00]/5" : "border border-[#262626] bg-[#0A0A0A] hover:border-[#666]"}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-heading font-bold uppercase text-white">{title}</span>
                  {cameraView === value && <Check className="h-4 w-4 text-[#CCFF00]" />}
                </div>
                <p className="mt-2 text-xs leading-relaxed text-[#A3A3A3]">{description}</p>
              </button>
            ))}
          </div>
          {error && (
            <div className="mt-5 flex gap-3 border border-[#FF3B30] bg-[#FF3B30]/10 p-4 text-sm text-[#FF3B30]">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {!capabilities.camera_mode_enabled && (
            <div className="mt-5 border border-[#FFD60A] bg-[#FFD60A]/10 p-4 text-sm text-[#FFD60A]">
              Live camera is disabled in this environment.
            </div>
          )}
          <button
            type="button"
            disabled={starting || !capabilities.camera_mode_enabled}
            onClick={() => onStart(cameraView)}
            className="mt-6 flex w-full items-center justify-center gap-2 bg-[#CCFF00] px-6 py-4 font-heading font-black uppercase tracking-wider text-black transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            {starting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Play className="h-5 w-5" />}
            Start {capabilities.target_shots}-shot session
          </button>
        </section>
      </main>
    </div>
  );
};

const ShotCard = ({ shot, onOpen, onRetry }) => {
  const complete = shot.status === "complete";
  const failed = shot.status === "failed";
  return (
    <div className="border border-[#262626] bg-[#121212] p-4" data-testid={`shot-card-${shot.shot_number}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#666]">Shot {shot.shot_number}</p>
          <p className={`mt-1 text-sm font-bold uppercase ${failed ? "text-[#FF3B30]" : complete ? "text-[#CCFF00]" : "text-white"}`}>
            {shotLabels[shot.stage] || shotLabels[shot.status] || shot.status}
          </p>
        </div>
        {complete ? (
          <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[#CCFF00] font-heading text-lg font-black text-[#CCFF00]">
            {shot.data?.overall_grade}
          </div>
        ) : failed ? (
          <AlertCircle className="h-5 w-5 text-[#FF3B30]" />
        ) : (
          <Loader2 className="h-5 w-5 animate-spin text-[#A3A3A3]" />
        )}
      </div>
      {!complete && !failed && (
        <div className="mt-3 h-1 overflow-hidden bg-[#262626]">
          <div className="h-full bg-[#CCFF00] transition-all" style={{ width: `${shot.progress}%` }} />
        </div>
      )}
      {failed && <p className="mt-2 line-clamp-2 text-xs text-[#A3A3A3]">{shot.error}</p>}
      <div className="mt-3 flex gap-2">
        {complete && (
          <button type="button" onClick={() => onOpen(shot)} className="flex flex-1 items-center justify-between border border-[#262626] px-3 py-2 text-xs font-bold uppercase text-white hover:border-[#CCFF00]">
            View analysis <ChevronRight className="h-4 w-4" />
          </button>
        )}
        {failed && (
          <button type="button" onClick={() => onRetry(shot.analysis_id)} className="flex flex-1 items-center justify-center gap-2 border border-[#FF3B30] px-3 py-2 text-xs font-bold uppercase text-[#FF3B30] hover:bg-[#FF3B30]/10">
            <RotateCcw className="h-4 w-4" /> Retry analysis
          </button>
        )}
      </div>
    </div>
  );
};

const CaptureReview = ({ seconds, automatic, onCancel, onSend }) => (
  <div className="absolute inset-x-4 bottom-4 z-30 border border-[#CCFF00] bg-[#0A0A0A]/95 p-4 shadow-2xl backdrop-blur">
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#CCFF00]/10">
        <Check className="h-5 w-5 text-[#CCFF00]" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-heading font-bold uppercase text-white">Swing captured</p>
        <p className="text-xs text-[#A3A3A3]">
          {automatic ? `Sending automatically in ${seconds}s` : "Review the detection, then send or discard it."}
        </p>
      </div>
      <button type="button" onClick={onCancel} className="flex items-center gap-1 border border-[#262626] px-3 py-2 text-xs font-bold uppercase text-white hover:border-[#FF3B30] hover:text-[#FF3B30]">
        <X className="h-4 w-4" /> Discard
      </button>
      {!automatic && (
        <button type="button" onClick={onSend} className="flex items-center gap-1 bg-[#CCFF00] px-3 py-2 text-xs font-bold uppercase text-black hover:bg-white">
          <Send className="h-4 w-4" /> Send
        </button>
      )}
    </div>
    {automatic && <div className="mt-3 h-1 overflow-hidden bg-[#262626]"><div className="h-full bg-[#CCFF00] transition-all duration-1000" style={{ width: `${(seconds / AUTO_SUBMIT_DELAY_SECONDS) * 100}%` }} /></div>}
  </div>
);

const CameraSession = () => {
  const [capabilities, setCapabilities] = useState(null);
  const [session, setSession] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [networkOnline, setNetworkOnline] = useState(navigator.onLine);
  const [pendingReview, setPendingReview] = useState(null);
  const [reviewSeconds, setReviewSeconds] = useState(AUTO_SUBMIT_DELAY_SECONDS);
  const [uploadingIds, setUploadingIds] = useState(new Set());
  const [pendingLocalCount, setPendingLocalCount] = useState(0);
  const [unreservedLocalCount, setUnreservedLocalCount] = useState(0);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const reviewTimerRef = useRef(null);
  const uploadsInFlightRef = useRef(new Set());

  useEffect(() => () => reviewTimerRef.current?.(), []);

  const sessionClosed = session && (
    session.status !== "active"
    || session.reserved_shots + unreservedLocalCount >= session.target_shots
  );
  const capturePaused = Boolean(sessionClosed || pendingReview || selectedAnalysis);

  const refreshSession = useCallback(async (sessionId) => {
    const response = await axios.get(`${API}/sessions/${sessionId}`);
    setSession(response.data);
    return response.data;
  }, []);

  const refreshLocalCaptures = useCallback(async (sessionId) => {
    const captures = sessionId ? await listPendingCaptures(sessionId) : [];
    setPendingLocalCount(captures.length);
    setUnreservedLocalCount(captures.filter((capture) => !capture.serverReserved).length);
    return captures;
  }, []);

  const uploadCapture = useCallback(async (capture) => {
    if (uploadsInFlightRef.current.has(capture.captureId)) return;
    uploadsInFlightRef.current.add(capture.captureId);
    setUploadingIds((current) => new Set(current).add(capture.captureId));
    try {
      await savePendingCapture(capture);
      await refreshLocalCaptures(capture.sessionId);
      const createdResponse = await axios.post(`${API}/sessions/${capture.sessionId}/shots`, {
        capture_id: capture.captureId,
        content_type: capture.contentType,
        byte_size: capture.blob.size,
        duration_ms: capture.durationMs,
        width: capture.width,
        height: capture.height,
        fps: capture.fps,
        capture_method: capture.captureMethod,
        detector_version: DETECTOR_VERSION,
      });
      const created = createdResponse.data;
      const reservedCapture = {
        ...capture,
        serverReserved: true,
        analysisId: created.analysis_id,
        shotNumber: created.shot_number,
      };
      await savePendingCapture(reservedCapture);
      await refreshLocalCaptures(capture.sessionId);
      if (created.upload_url) {
        await axios.put(created.upload_url, capture.blob, { headers: created.upload_headers });
        await axios.post(`${API}/analyses/${created.analysis_id}/start`);
      } else if (created.status === "failed") {
        await axios.post(`${API}/analyses/${created.analysis_id}/retry`);
      }
      await deletePendingCapture(capture.captureId);
      await refreshLocalCaptures(capture.sessionId);
      await refreshSession(capture.sessionId);
      setError(null);
    } catch (uploadError) {
      const permanentValidationFailure = await applyCaptureUploadFailurePolicy({
        error: uploadError,
        captureId: capture.captureId,
        sessionId: capture.sessionId,
        deleteCapture: deletePendingCapture,
        refreshCaptures: refreshLocalCaptures,
      });
      if (permanentValidationFailure) {
        setError(`${errorMessage(uploadError, "Camera clip was rejected.")} The clip cannot be uploaded; please record the swing again.`);
      } else {
        setError(`${errorMessage(uploadError, "Camera clip upload failed.")} The clip is saved locally and will retry when the connection returns.`);
      }
    } finally {
      uploadsInFlightRef.current.delete(capture.captureId);
      setUploadingIds((current) => {
        const next = new Set(current);
        next.delete(capture.captureId);
        return next;
      });
      refreshLocalCaptures(capture.sessionId).catch(() => {});
    }
  }, [refreshLocalCaptures, refreshSession]);

  const resumePendingUploads = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try {
      const captures = await refreshLocalCaptures(sessionId);
      if (!navigator.onLine) return;
      for (const capture of captures) await uploadCapture(capture);
    } catch (storageError) {
      setError(errorMessage(storageError, "Could not resume pending camera clips."));
    }
  }, [refreshLocalCaptures, uploadCapture]);

  useEffect(() => {
    axios.get(`${API}/capabilities`)
      .then(({ data }) => setCapabilities(data))
      .catch((capabilityError) => setError(errorMessage(capabilityError, "Could not load camera capabilities.")));
    pruneExpiredCaptures(LOCAL_CAPTURE_TTL_MS)
      .then(() => {
        const stored = loadStoredSession();
        if (!stored?.id) return;
        refreshSession(stored.id)
          .then(() => resumePendingUploads(stored.id))
          .catch(async () => {
            await deletePendingCapturesForSession(stored.id).catch(() => {});
            window.localStorage.removeItem(SESSION_STORAGE_KEY);
          });
      })
      .catch((storageError) => setError(errorMessage(storageError, "Could not inspect saved camera clips.")));
  }, [refreshSession, resumePendingUploads]);

  useEffect(() => {
    const online = () => {
      setNetworkOnline(true);
      if (session?.id) resumePendingUploads(session.id);
    };
    const offline = () => setNetworkOnline(false);
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
    };
  }, [resumePendingUploads, session?.id]);

  useEffect(() => {
    if (!session?.id) return undefined;
    const timer = setInterval(() => {
      refreshSession(session.id).catch((pollError) => {
        if (pollError.response?.status === 404) {
          setSession(null);
          window.localStorage.removeItem(SESSION_STORAGE_KEY);
        }
      });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [refreshSession, session?.id]);

  const startSession = useCallback(async (cameraView) => {
    setStarting(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/sessions`, { camera_view: cameraView });
      const state = {
        ...data,
        reserved_shots: 0,
        captured_shots: 0,
        completed_analyses: 0,
        terminal_analyses: 0,
        latest_shots: [],
      };
      setSession(state);
      window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ id: data.id, cameraView }));
    } catch (sessionError) {
      setError(errorMessage(sessionError, "Could not start camera session."));
    } finally {
      setStarting(false);
    }
  }, []);

  const submitPendingReview = useCallback(() => {
    if (!pendingReview) return;
    reviewTimerRef.current?.();
    reviewTimerRef.current = null;
    const capture = pendingReview;
    setPendingReview(null);
    uploadCapture(capture);
  }, [pendingReview, uploadCapture]);

  const cancelPendingReview = useCallback(() => {
    reviewTimerRef.current?.();
    reviewTimerRef.current = null;
    setPendingReview(null);
    setReviewSeconds(AUTO_SUBMIT_DELAY_SECONDS);
  }, []);

  const handleCapture = useCallback((captured) => {
    if (!session?.id) return;
    const capture = {
      ...captured,
      captureId: crypto.randomUUID(),
      sessionId: session.id,
      createdAt: Date.now(),
    };
    setPendingReview(capture);
    setReviewSeconds(AUTO_SUBMIT_DELAY_SECONDS);
    if (capabilities?.automatic_submission_enabled) {
      reviewTimerRef.current = beginAutoSubmissionCountdown({
        onTick: setReviewSeconds,
        onSubmit: () => {
          reviewTimerRef.current = null;
          setPendingReview(null);
          uploadCapture(capture);
        },
      });
    }
  }, [capabilities?.automatic_submission_enabled, session?.id, uploadCapture]);

  const camera = useSwingCamera({
    enabled: Boolean(session && !selectedAnalysis),
    paused: capturePaused,
    onCapture: handleCapture,
    maxClipBytes: capabilities?.max_clip_bytes || 20 * 1024 * 1024,
    maxClipDurationMs: capabilities?.max_clip_duration_ms || 12000,
  });

  const finishSession = useCallback(async () => {
    if (!session?.id) return;
    if (pendingLocalCount > 0) {
      setError("Wait for locally saved clips to finish uploading before ending the session.");
      return;
    }
    cancelPendingReview();
    try {
      const { data } = await axios.post(`${API}/sessions/${session.id}/finish`);
      setSession(data);
    } catch (finishError) {
      setError(errorMessage(finishError, "Could not finish the session."));
    }
  }, [cancelPendingReview, pendingLocalCount, session?.id]);

  const startNewSession = useCallback(() => {
    setSession(null);
    setSelectedAnalysis(null);
    setPendingReview(null);
    setPendingLocalCount(0);
    setUnreservedLocalCount(0);
    setError(null);
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
  }, []);

  const openAnalysis = useCallback(async (shot) => {
    try {
      const { data: video } = await axios.get(`${API}/analysis/${shot.analysis_id}/video`);
      setSelectedAnalysis({ ...shot.data, videoUrl: video.url });
    } catch (analysisError) {
      setError(errorMessage(analysisError, "Could not open this analysis."));
    }
  }, []);

  const retryAnalysis = useCallback(async (analysisId) => {
    try {
      await axios.post(`${API}/analyses/${analysisId}/retry`);
      await refreshSession(session.id);
    } catch (retryError) {
      setError(errorMessage(retryError, "Could not retry this analysis."));
    }
  }, [refreshSession, session?.id]);

  const currentStatus = useMemo(() => {
    if (capturePaused && pendingReview) return "Review captured swing";
    if (capturePaused && sessionClosed) return "Capture complete";
    return phaseLabels[camera.capturePhase] || "Camera ready";
  }, [camera.capturePhase, capturePaused, pendingReview, sessionClosed]);

  if (selectedAnalysis) {
    return <Dashboard data={selectedAnalysis} onNewAnalysis={() => setSelectedAnalysis(null)} newAnalysisLabel="Back to camera" />;
  }

  if (!capabilities) {
    return <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A]"><Loader2 className="h-8 w-8 animate-spin text-[#CCFF00]" /></div>;
  }

  if (!session) {
    return <CameraSetup capabilities={capabilities} onStart={startSession} starting={starting} error={error} />;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="camera-session">
      <header className="border-b border-[#262626] px-4 py-4 md:px-8">
        <div className="mx-auto flex max-w-[1500px] flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <BrandLogo compact />
            <span className="font-heading text-lg uppercase tracking-wider text-[#A3A3A3]">Live Cam</span>
          </div>
          <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-wider">
            <span className={`flex items-center gap-2 ${networkOnline ? "text-[#CCFF00]" : "text-[#FF3B30]"}`}>
              {networkOnline ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
              {networkOnline ? "Online" : "Offline"}
            </span>
            <span className="border-l border-[#262626] pl-3 text-[#A3A3A3]">
              {session.camera_view === "face_on" ? "Face on" : "Down the line"}
            </span>
            <button type="button" onClick={finishSession} disabled={session.status !== "active" || pendingLocalCount > 0} className="border border-[#262626] px-3 py-2 text-white hover:border-[#CCFF00] disabled:opacity-40">
              Finish early
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-[1500px] gap-4 p-4 md:p-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <section className="min-w-0">
          <div className="relative overflow-hidden border border-[#262626] bg-black camera-stage">
            <video ref={camera.videoRef} muted playsInline className="h-full w-full object-cover camera-preview" />
            <PoseOverlay
              poses={camera.poses}
              valid={camera.poseValid}
              width={camera.streamSettings?.width}
              height={camera.streamSettings?.height}
            />
            <div className="pointer-events-none absolute inset-[8%_13%] border border-dashed border-white/35">
              <div className="absolute left-1/2 top-[12%] h-16 w-16 -translate-x-1/2 rounded-full border border-white/25" />
              <div className="absolute bottom-[8%] left-1/2 top-[28%] -translate-x-1/2 border-l border-white/20" />
            </div>
            <div className="absolute left-4 top-4 flex flex-wrap gap-2">
              <span className={`flex items-center gap-2 border px-3 py-2 text-xs font-bold uppercase backdrop-blur ${camera.cameraStatus === "ready" ? "border-[#CCFF00] bg-black/70 text-[#CCFF00]" : "border-[#FFD60A] bg-black/70 text-[#FFD60A]"}`}>
                <Video className="h-4 w-4" /> {camera.cameraStatus}
              </span>
              <span className={`flex items-center gap-2 border px-3 py-2 text-xs font-bold uppercase backdrop-blur ${camera.detectorStatus === "ready" ? "border-white/30 bg-black/70 text-white" : "border-[#FFD60A] bg-black/70 text-[#FFD60A]"}`}>
                <Activity className="h-4 w-4" /> Detector {camera.detectorStatus}
              </span>
            </div>
            <div className="absolute inset-x-4 bottom-4 flex items-end justify-between gap-4">
              <div className="max-w-md bg-black/75 p-3 backdrop-blur">
                <p className="font-heading text-lg font-bold uppercase text-white">{currentStatus}</p>
                <p className="mt-1 text-xs text-[#A3A3A3]">{camera.poseStatus}</p>
              </div>
              <button
                type="button"
                onClick={camera.recordManually}
                disabled={capturePaused || camera.capturePhase === "validating" || camera.cameraStatus !== "ready"}
                className="pointer-events-auto flex shrink-0 items-center gap-2 bg-[#CCFF00] px-4 py-3 text-sm font-black uppercase text-black hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                <CircleStop className="h-5 w-5" /> Record next swing
              </button>
            </div>
            {pendingReview && (
              <CaptureReview
                seconds={reviewSeconds}
                automatic={capabilities.automatic_submission_enabled}
                onCancel={cancelPendingReview}
                onSend={submitPendingReview}
              />
            )}
          </div>

          {(error || camera.error) && (
            <div className="mt-4 flex items-start justify-between gap-4 border border-[#FF3B30] bg-[#FF3B30]/10 p-4 text-sm text-[#FF3B30]">
              <div className="flex gap-3"><AlertCircle className="h-5 w-5 shrink-0" /><span>{camera.error || error}</span></div>
              {camera.cameraStatus === "error" && (
                <button type="button" onClick={camera.startCamera} className="flex shrink-0 items-center gap-1 font-bold uppercase"><RefreshCw className="h-4 w-4" /> Restart</button>
              )}
            </div>
          )}

          {camera.warning && (
            <div className="mt-4 flex gap-3 border border-[#FFD60A] bg-[#FFD60A]/10 p-4 text-sm text-[#FFD60A]">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <span>{camera.warning}</span>
            </div>
          )}

          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <div className="border border-[#262626] bg-[#121212] p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#666]">Captured</p>
              <p className="mt-1 font-heading text-3xl font-black text-white">{session.captured_shots}<span className="text-base text-[#666]">/{session.target_shots}</span></p>
            </div>
            <div className="border border-[#262626] bg-[#121212] p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#666]">Analyzed</p>
              <p className="mt-1 font-heading text-3xl font-black text-[#CCFF00]">{session.completed_analyses}</p>
            </div>
            <div className="border border-[#262626] bg-[#121212] p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#666]">Local uploads</p>
              <p className="mt-1 flex items-center gap-2 font-heading text-3xl font-black text-white">
                {pendingLocalCount}{(pendingLocalCount > 0 || uploadingIds.size > 0) && <CloudUpload className="h-5 w-5 text-[#CCFF00]" />}
              </p>
            </div>
          </div>
        </section>

        <aside className="border border-[#262626] bg-[#0D0D0D] p-4">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#666]">Session</p>
              <h2 className="font-heading text-xl font-bold uppercase text-white">Latest five shots</h2>
            </div>
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#CCFF00] font-heading text-lg font-black text-black">
              {session.reserved_shots}
            </span>
          </div>
          <div className="space-y-3">
            {session.latest_shots.length === 0 ? (
              <div className="border border-dashed border-[#262626] px-4 py-10 text-center">
                <Camera className="mx-auto h-7 w-7 text-[#666]" />
                <p className="mt-3 text-sm text-[#A3A3A3]">Your captured swings will appear here.</p>
              </div>
            ) : session.latest_shots.map((shot) => (
              <ShotCard key={shot.analysis_id} shot={shot} onOpen={openAnalysis} onRetry={retryAnalysis} />
            ))}
          </div>

          {sessionClosed && (
            <div className="mt-4 border border-[#CCFF00] bg-[#CCFF00]/5 p-4">
              <p className="font-heading font-bold uppercase text-[#CCFF00]">
                {session.terminal_analyses === session.captured_shots ? "Session complete" : "Finishing analyses"}
              </p>
              <p className="mt-2 text-xs leading-relaxed text-[#A3A3A3]">
                {session.terminal_analyses === session.captured_shots
                  ? `${session.completed_analyses} results are ready.`
                  : `${session.captured_shots - session.terminal_analyses} shots are still processing.`}
              </p>
              {session.status === "complete" && (
                <button type="button" onClick={startNewSession} className="mt-4 flex w-full items-center justify-center gap-2 bg-[#CCFF00] px-4 py-3 text-sm font-black uppercase text-black hover:bg-white">
                  <RefreshCw className="h-4 w-4" /> Start new session
                </button>
              )}
            </div>
          )}
        </aside>
      </main>
    </div>
  );
};

export default CameraSession;
